import { Component, OnInit, AfterViewChecked, ElementRef } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { WidgetPaymentsService } from 'src/app/services/widget-payments.service';
import { FeeService } from 'src/app/services/fee.service';

type StatusFilter = 'ALL' | 'APPROVED' | 'PENDING' | 'REJECTED';

@Component({
  selector: 'app-widget-payment-history',
  templateUrl: './widget-payment-history.component.html',
  styleUrls: ['./widget-payment-history.component.css']
})
export class WidgetPaymentHistoryComponent implements OnInit, AfterViewChecked {

  private lastHeight = 0;

  personId = '';
  token = '';

  // Misma estructura que person-payments
  person: any = null;
  resp_paymentsCredits: any[] = [];
  banks: any = {};
  loading = true;
  error = '';
  errorType: 'auth' | 'permission' | 'network' | '' = '';

  // Filtros y paginación
  activeFilter: StatusFilter = 'ALL';
  searchTerm = '';
  pageSize = 5;
  displayCount = 5;

  // Visor de adjuntos
  viewingFilePaymentCode: string | null = null;

  autopago: any = [
    { key: 'BDV', name: 'AUTO: BANCO DE VENEZUELA' },
    { key: 'BA', name: 'AUTO: BANCAAMIGA' },
    { key: 'MERCHANT', name: 'AUTO: MEGASOFT' },
    { key: 'MC', name: 'AUTO: MERCANTIL' },
    { key: 'AUTO-DETECT', name: 'Auto detectado' }
  ];

  constructor(
    private route: ActivatedRoute,
    private widgetPaymentsService: WidgetPaymentsService,
    public feeService: FeeService,
    private el: ElementRef
  ) {}

  // postMessage resize para iframe embedding
  ngAfterViewChecked(): void {
    const h = this.el.nativeElement.scrollHeight;
    if (h > 0 && h !== this.lastHeight) {
      this.lastHeight = h;
      window.parent.postMessage({ type: 'widget-resize', height: h }, '*');
    }
  }

  ngOnInit(): void {
    document.body.style.background = 'transparent';
    document.documentElement.style.background = 'transparent';

    this.personId = this.route.snapshot.paramMap.get('personId') || '';
    this.token = this.route.snapshot.queryParamMap.get('token') || '';

    if (!this.personId || !this.token) {
      this.error = 'Parámetros de autenticación incompletos';
      this.loading = false;
      return;
    }

    this.loadPayments();
  }

  async loadPayments(): Promise<void> {
    this.loading = true;
    this.error = '';

    try {
      const result = await this.widgetPaymentsService.getPaymentHistory(
        this.personId,
        this.token
      );

      if (result.success) {
        this.person = result.data.person;
        this.resp_paymentsCredits = result.data.res || [];
        this.banks = result.data.banks || {};
      } else {
        this.error = 'No se pudieron cargar los pagos';
      }
    } catch (err: any) {
      const status = err?.status || err?.error?.statusCode;
      const msg = err?.error?.msg || err?.error?.message || err?.message || '';

      if (status === 401 || msg.includes('Token') || msg.includes('jwt') || msg.includes('expired')) {
        this.errorType = 'auth';
        this.error = 'La sesión ha expirado. Regresa al CRM y vuelve a abrir esta vista.';
      } else if (status === 403 || msg.includes('audience') || msg.includes('origin') || msg.includes('Forbidden')) {
        this.errorType = 'permission';
        this.error = 'Acceso denegado. Este widget no tiene permiso para acceder desde este origen.';
      } else if (status === 0 || !navigator.onLine) {
        this.errorType = 'network';
        this.error = 'Sin conexión con el servidor de pagos. Verifica tu red e intenta nuevamente.';
      } else {
        this.errorType = 'network';
        this.error = 'No se pudo cargar el historial de pagos. Intenta nuevamente.';
      }
    } finally {
      this.loading = false;
    }
  }

  // --- Filtros ---

  setFilter(filter: StatusFilter): void {
    this.activeFilter = filter;
    this.displayCount = this.pageSize;
  }

  get filteredPayments(): any[] {
    let list = this.resp_paymentsCredits;

    // Filtro por estatus
    if (this.activeFilter === 'APPROVED') {
      list = list.filter((i: any) => i?.payment?.status && i?.payment?.reconciled_payment);
    } else if (this.activeFilter === 'PENDING') {
      list = list.filter((i: any) => i?.payment?.status && !i?.payment?.reconciled_payment);
    } else if (this.activeFilter === 'REJECTED') {
      list = list.filter((i: any) => !i?.payment?.status);
    }

    // Búsqueda
    if (this.searchTerm.trim()) {
      const term = this.searchTerm.toLowerCase().trim();
      list = list.filter((i: any) => {
        const p = i?.payment;
        if (!p) return false;
        const code = (p.code || '').toLowerCase();
        const ref = (p.paymentMethodId || '').toLowerCase();
        const amount = (p.amount || 0).toString();
        const counterValue = (p.counterValue || 0).toString();
        const desc = (p.description || '').toLowerCase();
        const bankName = (this.banks[p.bankCode] || '').toLowerCase();
        const method = (p.account_payment?.payment_method?.name || '').toLowerCase();
        return code.includes(term) || ref.includes(term) || amount.includes(term) ||
               counterValue.includes(term) || desc.includes(term) || bankName.includes(term) || method.includes(term);
      });
    }

    return list;
  }

  get visiblePayments(): any[] {
    return this.filteredPayments.slice(0, this.displayCount);
  }

  get hasMore(): boolean {
    return this.displayCount < this.filteredPayments.length;
  }

  get filterCounts(): { all: number; approved: number; pending: number; rejected: number } {
    const all = this.resp_paymentsCredits;
    return {
      all: all.length,
      approved: all.filter((i: any) => i?.payment?.status && i?.payment?.reconciled_payment).length,
      pending: all.filter((i: any) => i?.payment?.status && !i?.payment?.reconciled_payment).length,
      rejected: all.filter((i: any) => !i?.payment?.status).length
    };
  }

  showMore(): void {
    this.displayCount += this.pageSize;
  }

  onSearchChange(): void {
    this.displayCount = this.pageSize;
  }

  // --- Visor de adjuntos ---

  toggleFileViewer(paymentCode: string): void {
    this.viewingFilePaymentCode = this.viewingFilePaymentCode === paymentCode ? null : paymentCode;
  }

  isFileVisible(paymentCode: string): boolean {
    return this.viewingFilePaymentCode === paymentCode;
  }

  getFileUrl(file: any): string {
    if (!file) return '';
    return typeof file === 'string' ? file : file.url || '';
  }

  isImage(file: any): boolean {
    const url = this.getFileUrl(file);
    const type = file?.type_file || '';
    if (type && type !== 'pdf') return true;
    return /\.(jpg|jpeg|png|gif|webp|bmp|svg)(\?|$)/i.test(url);
  }

  // Helpers copiados de person-payments (solo lectura, sin acciones)

  findAutopago = (acron: string) => {
    if (acron === 'BA' || acron === 'MC' || acron === 'BDV' || acron === 'AUTO-DETECT') {
      let autopago = this.autopago.find((ele: any) => ele.key === acron);
      if (autopago) {
        return autopago.name;
      } else {
        return null;
      }
    } else {
      return acron;
    }
  }

  validar_penalidad = (fees: any) => {
    return fees.some((row: any) => row?.apply_amount > 0);
  }

  formatDate(dateValue: any, format: 'datetime' | 'date' = 'datetime'): string {
    if (!dateValue) return '-';
    try {
      let date: Date | null = null;
      if (typeof dateValue === 'string') {
        date = new Date(dateValue);
      } else if (dateValue instanceof Date) {
        date = dateValue;
      } else if (typeof dateValue === 'number') {
        date = new Date(dateValue);
      } else if (typeof dateValue === 'object' && dateValue !== null) {
        const dateStr = dateValue.date || dateValue.value || dateValue.toString();
        if (dateStr) { date = new Date(dateStr); }
      }
      if (date && !isNaN(date.getTime())) {
        if (format === 'date') {
          return date.toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric' });
        } else {
          return date.toLocaleString('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: true });
        }
      }
      return 'Fecha inválida';
    } catch (error) {
      return 'Error en fecha';
    }
  }

  formatAsyncDate(dateValue: any): string {
    return this.formatDate(dateValue, 'datetime');
  }

  toDateFn(isoString: string): string {
    const [datePart] = isoString.split('T');
    const [year, month, day] = datePart.split('-');
    return `${day}/${month}/${year}`;
  }
}
